import { ChangeCircleOutlined } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import { Button, Dialog, DialogActions, DialogTitle, Tooltip } from '@mui/material';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Fragment } from 'react';
import ErrorUtil from '../../util/ErrorUtil';
import NumeroProcesso from '../NumeroProcesso';
import PainelFiltros from '../PainelFiltros';
import ReactComponent from '../ReactComponent';
import FiltroRapido from './FiltroRapido';
import OperacoesEmLoteSelecionar from './OperacoesEmLoteSelecionar';
import PainelProcessosOperacoesEmLote from './PainelProcessosOperacoesEmLote';
import { SITUACAO_ALTERACAO_MINUTA } from '@/dominio/SituacaoAlteracaoMinuta';
import SituacaoAlteracaoMinutaBusiness from '@/dominio/SituacaoAlteracaoMinutaBusiness';

class AtualizarDecisaoLote extends ReactComponent {
    dialogOpen = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            dialogOpen: observable,
            handleClickIcone: action,
            handleCloseDialog: action,
            processos: computed,
            processosSelecionados: computed,
            limparProcessoPesquisa: action,
            mensagemLoteSemProcessos: computed,
            handleClickReiniciar: action,
            renderConteudoDialog: computed
        });

        this.createReaction(
            reaction(
                () => this.dialogOpen,
                () => this.limparProcessoPesquisa()
            )
        );
    }

    handleClickIcone = () => {
        this.dialogOpen = true;
    };

    handleCloseDialog = () => {
        this.dialogOpen = false;
    };

    habilitar(registro) {
        if (!this.parametroStore.isOrgaoTST || registro.necessitaImportacaoSalaSessao || registro.certidaoGerada) {
            return false;
        }
        return (
            SituacaoAlteracaoMinutaBusiness.getStatusMinuta(registro)?.texto ===
            SITUACAO_ALTERACAO_MINUTA.ALTERADA.descricao
        );
    }

    get processos() {
        return (this.store.processosFiltrados || [])
            .filter((registro) => this.habilitar(registro))
            .filter((registro) => this.filtrarProcessoPeloNumero(registro));
    }

    get processosSelecionados() {
        return this.processosSelecionaveis.filter((registro) => this.store.isCheckedOperacoesEmLote(registro));
    }

    get processosSelecionaveis() {
        return this.processos;
    }

    limparProcessoPesquisa = () => {
        this.processoPesquisa = { numero: '', digito: '', ano: '', orgao: '', regional: '', vara: '' };
    };

    get mensagemLoteSemProcessos() {
        return 'Não há processos com alteração na decisão para serem reiniciados' + this.complementoMensagemFiltrando;
    }

    handleClickReiniciar = async () => {
        if (this.processosSelecionados.length > 0) {
            let qtdTotalProcessos = this.processosSelecionados.length;
            await this.store.processarLote({
                titulo: 'Reiniciando análise de decisão',
                qtdTotalProcessos: qtdTotalProcessos,
                lotesProcessos: this.processosSelecionados,
                funcaoOperacao: (registro) => this.reiniciarAnaliseDecisao(registro, qtdTotalProcessos)
            });
            if (qtdTotalProcessos >= 10) {
                this.sessaoStore.recarregarListaProcessos(true, true);
            }
        } else {
            this.mensagemStore.abrirMensagemAlerta('Selecione um ou mais processos');
        }
    };

    reiniciarAnaliseDecisao = async (registro, qtdTotalProcessos) => {
        const resultado = await this.sessaoService.reiniciarAnaliseDecisao(registro);

        if (resultado && !ErrorUtil.isResultadoErro(resultado)) {
            if (qtdTotalProcessos < 10) {
                this.sessaoStore.recarregarProcesso(registro, true);
            }
            runInAction(() => (registro.necessitaImportacaoSalaSessao = false));
        }

        this.store.incrementarProgressoLote();

        return resultado;
    };

    render() {
        if (!this.parametroStore.isOrgaoTST) {
            return null;
        }
        return (
            <div>
                <div
                    style={{ marginRight: '10px' }}
                    className="corBotao"
                >
                    <Tooltip
                        title={'Atualizar decisão e remover indicador de minuta alterada em lote'}
                        placement="top-start"
                    >
                        <ChangeCircleOutlined
                            onClick={this.handleClickIcone}
                            style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                        />
                    </Tooltip>
                </div>
                {this.dialogOpen && (
                    <Dialog
                        fullWidth
                        maxWidth="xl"
                        onClose={this.handleCloseDialog}
                        open={this.dialogOpen}
                        PaperProps={{ style: { height: '100%' } }}
                    >
                        <DialogTitle
                            id="simple-dialog-title"
                            style={{ padding: '16px 24px 0px' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        flexGrow: 1
                                    }}
                                >
                                    <h4 style={{ margin: '0px' }}>Atualizar decisão</h4>
                                    <div style={{ margin: '0px 10px' }}>
                                        <NumeroProcesso
                                            dense
                                            tooltip="Número do processo a ser pesquisado na listagem"
                                            callbackOnChange={this.callbackNumeroProcesso}
                                            clickLimpar={this.limparProcessoPesquisa}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                margin: '10px',
                                                paddingTop: '5px'
                                            }}
                                        >
                                            <PainelFiltros />
                                        </div>
                                        <FiltroRapido id="filtroRapidoReiniciarAnaliseDecisao" />
                                    </div>
                                </div>
                            </div>
                            <div style={{ marginTop: '15px' }}>
                                Os textos das novas decisões serão copiados sobre os atuais rascunhos das certidões para
                                os processos selecionados.
                            </div>
                        </DialogTitle>
                        {this.renderConteudoDialog}
                        <DialogActions>
                            <Button
                                onClick={this.handleCloseDialog}
                                color="secondary"
                                variant="outlined"
                                endIcon={<CancelIcon />}
                            >
                                Fechar
                            </Button>
                            <Button
                                onClick={this.handleClickReiniciar}
                                color="primary"
                                variant="outlined"
                                endIcon={<ChangeCircleOutlined />}
                            >
                                Atualizar
                            </Button>
                        </DialogActions>
                    </Dialog>
                )}
            </div>
        );
    }

    get renderConteudoDialog() {
        return (
            <Fragment>
                <OperacoesEmLoteSelecionar
                    processosSelecionaveis={this.processosSelecionaveis}
                    processos={this.processos}
                />
                <PainelProcessosOperacoesEmLote
                    processos={this.processos}
                    mensagemLoteSemProcessos={this.mensagemLoteSemProcessos}
                />
            </Fragment>
        );
    }
}

export default inject('stores')(observer(AtualizarDecisaoLote));
